/**
 * Embedded-WebView detection + escape banner.
 *
 * Google's OAuth servers return `Error 403: disallowed_useragent` for ANY
 * request originating from an in-app browser (Instagram, FB, FB Messenger,
 * LinkedIn, Twitter/X, TikTok, WhatsApp, Discord, etc.) under their
 * "Use Secure Browsers" policy — regardless of how our app launches the
 * OAuth request.
 *
 * The only fix is to detect the in-app browser at click time and ask the
 * user to open the page in their real browser instead. Email/password
 * sign-in continues to work fine; only "Continue with Google" is blocked.
 *
 * Detection is purely based on User-Agent string. We deliberately use a
 * conservative allowlist of explicit substrings so we never falsely flag
 * a legitimate Chrome / Safari / Firefox session.
 */

// Known in-app browser markers in the user-agent. Conservative — we
// intentionally do NOT flag generic terms like "WebKit" or "Mobile".
const EMBEDDED_UA_PATTERNS = [
  /\bInstagram\b/i,
  /\bFBAN|FBAV|FBIOS|FB_IAB|FBSV\b/i,        // Facebook + Messenger
  /\bMessengerLite\b/i,
  /\bLinkedInApp\b/i,
  /\bTwitter\b/i, /\bTwitterAndroid\b/i,
  /\bTikTok\b/i, /\bBytedanceWebview\b/i,
  /\bWhatsApp\b/i,
  /\bWeChat\b|\bMicroMessenger\b/i,
  /\bSnapchat\b/i,
  /\bPinterest\b/i,
  /\bLine\b\//i,                             // LINE messenger
  /\bDiscordBot|DiscordCanary|DiscordPTB\b/i,
  /\bKakaoTalk\b/i,
  /\bDouban\b|\bWeibo\b/i,
  // Android WebView signature: "; wv)" — only flag when present
  // alongside an Android UA, never on its own.
  /Android.*;\s*wv\)/i,
];

/**
 * @returns {{ isEmbedded: boolean, label: string | null }}
 *   isEmbedded — true if the current UA is a known in-app browser
 *   label      — friendly name of the app (or null) for UI copy
 */
export const detectEmbeddedBrowser = () => {
  if (typeof navigator === 'undefined' || !navigator.userAgent) {
    return { isEmbedded: false, label: null };
  }
  const ua = navigator.userAgent;

  // Friendly labels — picked from whichever pattern matches first.
  const friendlyMap = [
    [/\bInstagram\b/i, 'Instagram'],
    [/\bFBAN|FBAV|FBIOS|FB_IAB|FBSV\b/i, 'Facebook'],
    [/\bMessengerLite\b/i, 'Messenger'],
    [/\bLinkedInApp\b/i, 'LinkedIn'],
    [/\bTwitter\b/i, 'Twitter / X'],
    [/\bTikTok\b/i, 'TikTok'],
    [/\bWhatsApp\b/i, 'WhatsApp'],
    [/\bWeChat\b|\bMicroMessenger\b/i, 'WeChat'],
    [/\bSnapchat\b/i, 'Snapchat'],
    [/\bPinterest\b/i, 'Pinterest'],
    [/\bLine\b\//i, 'LINE'],
    [/\bDiscord\b/i, 'Discord'],
    [/\bKakaoTalk\b/i, 'KakaoTalk'],
  ];

  for (const [pattern, label] of friendlyMap) {
    if (pattern.test(ua)) return { isEmbedded: true, label };
  }
  // Generic Android WebView fallback (Slack, custom corporate browsers).
  if (/Android.*;\s*wv\)/i.test(ua) || EMBEDDED_UA_PATTERNS.some((p) => p.test(ua))) {
    return { isEmbedded: true, label: 'in-app browser' };
  }
  return { isEmbedded: false, label: null };
};

/**
 * Open the current URL in the device's real browser, escaping the in-app
 * WebView. Best-effort — falls back to copying the URL if nothing fires.
 *
 * Android: uses an `intent://` URL that the OS resolves to Chrome.
 * iOS    : `x-safari-https://` opens Safari for HTTPS URLs.
 * Other  : copies the URL to clipboard and shows a toast.
 */
export const openInRealBrowser = (toastFn) => {
  if (typeof window === 'undefined') return;
  const ua = navigator.userAgent || '';
  const full = window.location.href;
  const noScheme = full.replace(/^https?:\/\//, '');

  if (/Android/i.test(ua)) {
    // Android Chrome intent — tells the OS "open this in a regular browser"
    const intentUrl =
      `intent://${noScheme}#Intent;scheme=https;package=com.android.chrome;end`;
    window.location.href = intentUrl;
    return;
  }
  if (/iPhone|iPad|iPod/i.test(ua)) {
    // iOS Safari deep link
    window.location.href = `x-safari-https://${noScheme}`;
    return;
  }
  // Desktop / unknown — copy to clipboard
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(full).then(() => {
      if (toastFn) toastFn('Link copied — paste it into Chrome, Safari, or Firefox to continue.');
    }).catch(() => {
      if (toastFn) toastFn(`Open this URL in your browser: ${full}`);
    });
  } else if (toastFn) {
    toastFn(`Open this URL in your browser: ${full}`);
  }
};
